import { SuspenseSelect } from '@/components/Suspense';
import {
  BaseSelectProps,
  useSetCacheQueryData,
} from '@/context/SelectComponent/Base';
import { useModelsToOptionsWithCacheItems } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import { Project, useGetProjectsSuspenseInfinite } from 'ap-openapi';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'comboBox'
  >;
  query?: string;
  per?: number;
  validateMaxCount?: number;
  comboBox?: boolean;
  setLabelField?: (label: string) => void;
  displayInactiveOptions?: boolean;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'> &
  BaseSelectProps;

const _ProjectSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  onChangeAfter,
  setLabelField,
  cacheQueryKey,
  per = 25,
  comboBox = true,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useGetProjectsSuspenseInfinite(
    {
      query,
      per: per,
    },
    {
      query: {
        getNextPageParam: (page) => {
          const url = new URL(page.data.next ?? '/', location.href);
          if (!url.searchParams.has('page')) return undefined;
          return Number(url.searchParams.get('page'));
        },
      },
      request: {
        headers: {
          agentApplicantResource: 'project',
        },
      },
    }
  );
  if (error) throw error;
  const data = useMemo(() => getPageData(apiData, 'projects'), [apiData]);
  const options = useModelsToOptionsWithCacheItems<Project>(
    data,
    cacheQueryKey,
    query,
    defaultOptions
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const selectProps: SelectProps = useMemo(() => {
    return {
      onSearchDebounceWait: 600,
      comboBox: comboBox,
      placeholder: t('project_placeholder'),
      inputPlaceholder: t('project_input_placeholder'),
      ..._selectProps,
      options: options,
      onSearch: onSearch,
      loading: isLoading,
      fetchMore: fetchMore,
      mode: 'single',
      selectAll: undefined,
      selectAllLabel: undefined,
    };
  }, [comboBox, t, _selectProps, options, onSearch, isLoading, fetchMore]);

  useSetCacheQueryData(cacheQueryKey, data);

  const wrapOnChangeAfter = useCallback(
    (value: string | number | (string | number)[]) => {
      const selectedOption = options.find(
        (option) => option.value === String(value)
      );
      onChangeAfter?.(value);
      setLabelField?.(selectedOption?.label ?? '');
    },
    [setLabelField, options, onChangeAfter]
  );

  return (
    <FormSelect<TFieldValues>
      selectProps={selectProps}
      {...rest}
      onChangeAfter={wrapOnChangeAfter}
    />
  );
};
_ProjectSelect.displayName = '_ProjectSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ProjectSelect = memo((props) => (
  <SuspenseSelect>
    <_ProjectSelect {...props} />
  </SuspenseSelect>
)) as typeof _ProjectSelect;
ProjectSelect.displayName = 'ProjectSelect';
